import React, { useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { getContactsWithRevenue, StatFilterPayload } from '../../../services'
import { Typogaphy } from 'vnetwork-ui'
import { formatCurrency, formatFromUTC } from '@/utils/function'
import {
    DataTableCustomized,
    DataTableColumn,
} from '@/components/ui/data-table-customized'
import { ContactSource } from '../../../types/response'
import { ContactChanel } from '@/modules/Marketing/types/utils'
import MoneyIcons from '@/icons/MoneyIcons'
import ContactDetailModal from '../../../../../modals/ContactMarketingModals/Detail'
import { TableSkeleton } from './skeletons'

const PAGE_SIZE = 10

interface ContactsWithRevenueTableProps {
    filterPayload: StatFilterPayload
}

const ContactsWithRevenueTable: React.FC<ContactsWithRevenueTableProps> = ({
    filterPayload,
}) => {
    const { t } = useTranslation('marketing')
    const [page, setPage] = useState(1)
    const [pageSize, setPageSize] = useState(PAGE_SIZE)
    const [selectedContactId, setSelectedContactId] = useState<number | null>(
        null
    )
    const [isModalOpen, setIsModalOpen] = useState(false)

    const handleOpenContact = (contactSourceId: number) => {
        setSelectedContactId(contactSourceId)
        setIsModalOpen(true)
    }

    const handleCloseModal = () => {
        setIsModalOpen(false)
        setSelectedContactId(null)
    }

    const queryPayload = useMemo(
        () => ({
            ...filterPayload,
            page,
            page_size: pageSize,
        }),
        [filterPayload, page, pageSize]
    )

    const { data, isLoading, renderState } = useQueryWithHandler(
        {
            queryKey: [
                QUERY_KEYS.MARKETING,
                'contacts-with-revenue',
                queryPayload,
            ],
            queryFn: () => getContactsWithRevenue(queryPayload),
        },
        { loadingComponent: <TableSkeleton /> }
    )

    const contacts = data?.results || []
    const total = data?.pagination?.total || 0
    const totalRevenue = data?.total_revenue || 0

    const columns: DataTableColumn<ContactSource>[] = useMemo(
        () => [
            {
                key: 'contact_source_company',
                title: t('company'),
                width: 250,
                headerClassName: 'capitalize',
                cellClassName: 'text-primary',
                render: (_, row) => (
                    <span className="uppercase truncate">
                        {row?.contact_source_company ||
                            row?.contact_source_name}
                    </span>
                ),
            },
            {
                key: 'contact_source_assignee',
                title: t('contact-source-assignee'),
                width: 150,
                render: (_, row) =>
                    row?.contact_source_assignee_name
                        ?.map((item) => item?.employee_name)
                        ?.join(', ') || '---',
            },
            {
                key: 'contact_source_channel',
                title: t('contact-chanel-filter'),
                width: 100,
                cellClassName: 'text-right',
                headerClassName: 'text-right',
                render: (_, row) => {
                    const channel = ContactChanel(t)?.find(
                        (channel) =>
                            channel?.value ===
                            row?.contact_source_channel?.toLowerCase()
                    )
                    return channel?.label || row?.contact_source_channel
                },
            },
            {
                key: 'revenue',
                title: t('revenue'),
                width: 130,
                headerClassName: 'text-right',
                cellClassName: 'text-right',
                render: (_, row) => (
                    <span className="text-green-600">
                        {row?.revenue ? formatCurrency(row?.revenue) : '---'}
                    </span>
                ),
            },
            {
                key: 'contact_source_created_at',
                title: t('created-at'),
                width: 120,
                headerClassName: 'text-right',
                cellClassName: 'text-right',
                render: (_, row) =>
                    formatFromUTC(row?.contact_source_created_at) || '---',
            },
        ],
        [t]
    )

    const stateComponent = renderState()
    if (stateComponent) return stateComponent

    return (
        <div className="bg-white rounded-md border border-gray-200 p-4">
            <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                    <MoneyIcons className="text-primary w-4 h-4" />
                    <Typogaphy className="font-medium text-primary capitalize">
                        {t('contacts-with-revenue')}
                    </Typogaphy>
                </div>
                <div className="flex items-center gap-2 px-3 rounded-full">
                    <Typogaphy className="text-xs capitalize text-primary font-medium">
                        {t('total')}:
                    </Typogaphy>
                    <Typogaphy className="text-xs font-medium text-primary">
                        {totalRevenue > 0
                            ? formatCurrency(totalRevenue)
                            : '---'}
                    </Typogaphy>
                </div>
            </div>

            <DataTableCustomized<ContactSource>
                columns={columns}
                rows={contacts}
                loading={isLoading}
                emptyMessage={t('no-data')}
                rowKey={(row) => String(row.contact_source_id)}
                onRowClick={(row) => handleOpenContact(row.contact_source_id)}
                pagination={{
                    page,
                    pageSize,
                    total,
                    onPageChange: setPage,
                    onPageSizeChange: (size) => {
                        setPageSize(size)
                        setPage(1)
                    },
                    pageSizeOptions: [10, 20, 50],
                }}
            />

            <ContactDetailModal
                open={isModalOpen}
                onClose={handleCloseModal}
                contactSourceID={selectedContactId}
            />
        </div>
    )
}

export default ContactsWithRevenueTable
